import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Circle,
  GoogleMap,
  OverlayView,
  Polygon,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";

import { Button, Spinner } from "../../../components/ui";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../../../config/googleMaps";
import { brand, colors } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import type {
  MonitoringCorridor,
  MonitoringGeofence,
  MonitoringUnitRow,
} from "../hooks/useMonitoringCenter";
import {
  clusterByPixelGrid,
  geofenceColor,
  MEXICO_CENTER,
  tacticalMapStyles,
} from "../services/tacticalMap";
import { WATCH_STATUS_MAP_COLORS } from "../theme";
import { WATCH_STATUSES } from "../types";
import RastroDelDia from "./RastroDelDia";
import MapFilterMenu, { type MapFilterGroup } from "./MapFilterMenu";
import TacticalCrosshair from "./TacticalCrosshair";
import TacticalFocus from "./TacticalFocus";

const INITIAL_ZOOM = 5;
const CLUSTER_GRID_PX = 58;
const GEOFENCE_LABEL_ZOOM = 8;
/**
 * Acercamiento al seleccionar una unidad.
 *
 * Estaba en 19, que es nivel de banqueta. A esa escala el radar de
 * `TacticalFocus` no se ve: sus anillos son de 1, 2 y 5 km y a zoom 19 el más
 * chico cae muy fuera de la pantalla, así que el barrido se dibujaba en un
 * lienzo donde nada era visible. En 13 el anillo de 1 km ocupa un tercio del
 * alto del mapa y el barrido se ve completo, que es para lo que existe.
 */
const FOCUS_ZOOM = 13;
const FIT_MAX_ZOOM = 9;
const URGENT_STATUSES = new Set(["alert", "risk"]);

/**
 * Los estados que LATEN sobre el mapa.
 *
 * `warning` se sumó a los dos urgentes. Con sólo el cambio de color, una unidad
 * que acababa de pasar a advertencia era un punto ámbar entre puntos verdes: si
 * no estabas mirando ese pedazo del mapa en ese instante, no te enterabas. El
 * latido no grita, pero el ojo lo caza de reojo, que es justo lo que hace falta
 * al abrir el centro de monitoreo y barrer la pantalla sin nada seleccionado.
 */
const LATIENDO = new Set(["warning", "alert", "risk"]);

type PositionedRow = MonitoringUnitRow & { position: { lat: number; lng: number } };

function TacticalClock() {
  const { i18n } = useTranslation("common");
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <span className="pointer-events-none absolute right-3 top-3 z-[4] font-mono text-[12px] tabular-nums text-primary-500">
      {now.toLocaleTimeString(i18n.language, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })}
    </span>
  );
}

export interface TacticalMapProps {
  rows: MonitoringUnitRow[];
  totalUnits: number;
  geofences: MonitoringGeofence[];
  corridors: MonitoringCorridor[];
  openCases: number;
  selectedUnitId: string | null;
  onSelectUnit: (unitId: string | null) => void;
  /** Grupos de filtro que se cuelgan del botón del mapa. */
  filterGroups?: MapFilterGroup[];
  activeFilterCount?: number;
  onClearFilters?: () => void;
  /** Se llama con el caso del rombo tocado en el rastro. */
  onSelectCase?: (caseId: string) => void;
  /** El caso abierto, para marcar su rombo con el radar. */
  activeCaseId?: string | null;
  /**
   * El rastro es un estado COMPARTIDO con la sección, no del mapa.
   *
   * El mismo interruptor abre la capa sobre el mapa y el panel de inteligencia
   * en vivo a la derecha: son las dos mitades de una misma intención —«ponme en
   * modo análisis»— y tenerlas en dos botones obligaba a recordar cuál era cuál.
   */
  verRastro?: boolean;
  onToggleRastro?: () => void;
  /** Qué unidad dibuja el rastro. Se elige en la lista, no acá. */
  rastroDeviceId?: string | null;
  /** Cuántas horas hacia atrás dibuja el rastro. Se elige en la lista. */
  rastroHoras?: number;
}

export default function TacticalMap({
  rows,
  totalUnits,
  geofences,
  corridors,
  openCases,
  selectedUnitId,
  onSelectUnit,
  onSelectCase,
  activeCaseId = null,
  verRastro = false,
  onToggleRastro,
  rastroDeviceId = null,
  rastroHoras = 6,
  filterGroups = [],
  activeFilterCount = 0,
  onClearFilters,
}: TacticalMapProps) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const [satellite, setSatellite] = useState(false);
  const [showGeofences, setShowGeofences] = useState(true);
  const [showCorridors, setShowCorridors] = useState(true);
  const hasFitted = useRef(false);

  const palette = isDarkMode ? colors.dark : colors.light;

  const options = useMemo<google.maps.MapOptions>(
    () => ({
      styles: satellite ? undefined : tacticalMapStyles(isDarkMode),
      mapTypeId: satellite ? "hybrid" : "roadmap",
      disableDefaultUI: true,
      zoomControl: true,
      clickableIcons: false,
      gestureHandling: "greedy",
      backgroundColor: palette.bg,
    }),
    [satellite, isDarkMode, palette.bg],
  );

  const positioned = useMemo<PositionedRow[]>(
    () => rows.filter((row): row is PositionedRow => row.position !== null),
    [rows],
  );

  const selectedRow = useMemo(
    () => positioned.find((row) => row.unitId === selectedUnitId) ?? null,
    [positioned, selectedUnitId],
  );

  const selectedRef = useRef(selectedRow);
  selectedRef.current = selectedRow;

  const selectedIsListed = useMemo(
    () => (selectedUnitId ? rows.some((row) => row.unitId === selectedUnitId) : false),
    [rows, selectedUnitId],
  );

  const focusedUnitId = useRef<string | null>(null);
  const selectedLat = selectedRow?.position.lat ?? null;
  const selectedLng = selectedRow?.position.lng ?? null;

  const clusters = useMemo(
    () =>
      clusterByPixelGrid(
        positioned
          .filter((row) => row.unitId !== selectedUnitId)
          .map((row) => ({ id: row.unitId, position: row.position, status: row.status, row })),
        zoom,
        CLUSTER_GRID_PX,
      ),
    [positioned, zoom, selectedUnitId],
  );

  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("zoom_changed", () => setZoom(map.getZoom() ?? INITIAL_ZOOM));
    return () => listener.remove();
  }, [map]);

  useEffect(() => {
    if (!map || hasFitted.current || positioned.length === 0) return;
    if (selectedRef.current) return;
    hasFitted.current = true;

    const bounds = new google.maps.LatLngBounds();
    positioned.forEach((row) => bounds.extend(row.position));
    map.fitBounds(bounds, 56);

    google.maps.event.addListenerOnce(map, "idle", () => {
      const current = map.getZoom() ?? INITIAL_ZOOM;
      if (!selectedRef.current && current > FIT_MAX_ZOOM) map.setZoom(FIT_MAX_ZOOM);
      setZoom(map.getZoom() ?? INITIAL_ZOOM);
    });
  }, [map, positioned]);

  /*
    Mientras se está mirando un hecho, el mapa NO persigue a la unidad.

    El `focoManual` de arriba sólo saltaba el primer recentrado. Con la unidad
    en marcha, cada posición nueva movía el mapa otra vez y el rombo que se
    acababa de abrir se iba de cuadro: se veía como si el mapa peleara consigo
    mismo. Un hecho es un punto FIJO en el pasado; seguir a la unidad mientras
    se mira no tiene sentido.

    Se suelta al cerrar el caso —que es cuando `activeCaseId` vuelve a null— y
    entonces el seguimiento se reanuda solo.
  */
  const [mirandoUnHecho, setMirandoUnHecho] = useState(false);

  useEffect(() => {
    if (!activeCaseId) setMirandoUnHecho(false);
  }, [activeCaseId]);

  useEffect(() => {
    if (!selectedUnitId) {
      focusedUnitId.current = null;
      return;
    }
    if (!map || selectedLat == null || selectedLng == null) return;
    if (mirandoUnHecho) return;

    // Alguien acaba de enfocar un hecho a mano: se respeta y se da la unidad
    // por «ya enfocada», para que el siguiente movimiento sea una panorámica
    // suave y no un salto.
    if (focoManual.current) {
      focoManual.current = false;
      focusedUnitId.current = selectedUnitId;
      return;
    }

    // Al cambiar de unidad se centra Y se acerca de golpe; mientras siga la
    // misma seleccionada, se sigue con una panorámica suave.
    //
    // Antes era `setZoom` y después `panTo`: el zoom recentraba sobre el centro
    // viejo y la panorámica posterior, con un salto de ocho niveles, la resolvía
    // Google como un brinco. La unidad terminaba fuera de cuadro justo cuando
    // arrancaba el barrido del radar.
    if (focusedUnitId.current !== selectedUnitId) {
      focusedUnitId.current = selectedUnitId;
      map.setCenter({ lat: selectedLat, lng: selectedLng });
      map.setZoom(FOCUS_ZOOM);
      return;
    }

    map.panTo({ lat: selectedLat, lng: selectedLng });
  }, [map, selectedUnitId, selectedLat, selectedLng, mirandoUnHecho]);

  const corridorOptions = useMemo<google.maps.PolylineOptions | null>(() => {
    if (!isLoaded) return null;
    return {
      strokeColor: brand.primary,
      strokeOpacity: 0,
      clickable: false,
      icons: [
        {
          icon: { path: "M 0,-1 0,1", strokeOpacity: 0.75, scale: 3, strokeColor: brand.primary },
          offset: "0",
          repeat: "16px",
        },
      ],
    };
  }, [isLoaded]);

  /*
    ── El rastro del día ─────────────────────────────────────────────────────
    Una lista de casos contesta «qué pasó» y nunca «dónde», y en una flota lo
    segundo suele explicar lo primero: cinco cortes de señal repartidos por la
    ciudad son un problema del equipo; los mismos cinco en el mismo tramo son
    cobertura.

    La unidad se elige AQUÍ y no hace falta haberla seleccionado antes: quien
    abre el rastro normalmente todavía no sabe cuál quiere mirar, y exigir una
    selección previa pedía dos gestos para una sola intención.

    ⚠️ Estos hooks van ARRIBA de los `return` tempranos de `loadError` y
    `!isLoaded`. Estuvieron debajo y la pantalla se quedó en negro: con el mapa
    aún cargando React ejecutaba menos hooks que en el render siguiente
    —«Rendered more hooks than during the previous render»— y el árbol entero
    se desmonta.
  */

  /*
    Un foco a mano —tocar un rombo del rastro— tiene que ganarle al centrado
    automático de la unidad.

    El orden importa y no se ve: al tocar un rombo primero se centra el mapa en
    el hecho, y enseguida se selecciona su caso, lo que selecciona su unidad, lo
    que dispara el efecto de abajo… que recentraba sobre la unidad. El resultado
    era que el primer clic parecía ignorar el rombo y saltar al camión.
  */
  const focoManual = useRef(false);



  if (loadError) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-subtle p-6 text-center">
        <p className="text-xs text-content-muted">{t("monitoreoIa.monitoringCenter.mapError")}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-subtle">
        <Spinner size="lg" className="text-primary-500" />
      </div>
    );
  }

  const layers = [
    {
      id: "satellite",
      active: satellite,
      icon: "ri-earth-line",
      label: t("monitoreoIa.monitoringCenter.layers.satellite"),
      toggle: () => setSatellite((value) => !value),
    },
    {
      id: "geofences",
      active: showGeofences,
      icon: "ri-shape-2-line",
      label: t("monitoreoIa.monitoringCenter.layers.geofences"),
      toggle: () => setShowGeofences((value) => !value),
    },
    {
      id: "corridors",
      active: showCorridors,
      icon: "ri-route-line",
      label: t("monitoreoIa.monitoringCenter.layers.trips"),
      toggle: () => setShowCorridors((value) => !value),
    },
  ];

  return (
    <div className="relative h-full w-full overflow-hidden bg-subtle">
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={MEXICO_CENTER}
        zoom={INITIAL_ZOOM}
        options={options}
        onLoad={setMap}
        onUnmount={() => setMap(null)}
        onClick={() => onSelectUnit(null)}
      >
        {/* El rastro ya NO depende del panel de inteligencia: se pide desde la
            fila de la unidad y se dibuja aunque el panel esté plegado. Eran
            dos cosas distintas atadas al mismo interruptor, y apagar la lista
            de casos borraba el recorrido que alguien estaba analizando. */}
        {rastroDeviceId ? (
          <RastroDelDia
            deviceId={rastroDeviceId}
            horas={rastroHoras}
            casoActivoId={activeCaseId ?? null}
            onCaso={onSelectCase}
            onFoco={(pos) => {
              if (!map) return;
              focoManual.current = true;
              setMirandoUnHecho(true);
              // El mismo gesto que al seleccionar una unidad: centrar y acercar
              // de golpe. Un `panTo` suave desde lejos se ve como un barrido
              // largo y quien tocó el rombo pierde de vista qué tocó.
              map.setCenter(pos);
              map.setZoom(FOCUS_ZOOM);
            }}
          />
        ) : null}

        {showCorridors && corridorOptions
          ? corridors.map((corridor) => (
              <Polyline key={corridor.id} path={corridor.path} options={corridorOptions} />
            ))
          : null}

        {showGeofences
          ? geofences.map((geofence) => {
              const color = geofenceColor(geofence.source);
              const shape = {
                strokeColor: color,
                strokeOpacity: 0.85,
                strokeWeight: 1.4,
                fillColor: color,
                fillOpacity: 0.1,
                clickable: false,
              };

              return (
                <Fragment key={geofence.id}>
                  {geofence.geometry.kind === "circle" ? (
                    <Circle
                      center={geofence.geometry.center}
                      radius={geofence.geometry.radius}
                      options={shape}
                    />
                  ) : (
                    <Polygon paths={geofence.geometry.path} options={shape} />
                  )}
                </Fragment>
              );
            })
          : null}

        {showGeofences
          ? geofences.map((geofence) => {
              const color = geofenceColor(geofence.source);
              return (
                <OverlayView
                  key={`label-${geofence.id}`}
                  position={geofence.geometry.center}
                  mapPaneName={OverlayView.OVERLAY_LAYER}
                >
                  <div className="absolute">
                    <span
                      className="absolute left-1/2 top-1/2 h-3 w-px -translate-x-1/2 -translate-y-1/2"
                      style={{ background: color, opacity: 0.9 }}
                    />
                    <span
                      className="absolute left-1/2 top-1/2 h-px w-3 -translate-x-1/2 -translate-y-1/2"
                      style={{ background: color, opacity: 0.9 }}
                    />
                    {zoom >= GEOFENCE_LABEL_ZOOM ? (
                      <span
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 whitespace-nowrap rounded border px-1 font-mono text-[8.5px] max-sm:text-[11px] leading-[14px] tracking-wide"
                        style={{
                          color,
                          borderColor: color,
                          background: "rgb(var(--c-surface) / 0.85)",
                        }}
                      >
                        {geofence.name}
                      </span>
                    ) : null}
                  </div>
                </OverlayView>
              );
            })
          : null}

        {clusters.map((cluster) => {
          const tone = WATCH_STATUS_MAP_COLORS[cluster.status];

          if (cluster.members.length === 1) {
            const row = cluster.members[0].row;
            return (
              <OverlayView
                key={row.unitId}
                position={row.position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <UnitMarker row={row} tone={tone} selected={false} onSelect={onSelectUnit} />
              </OverlayView>
            );
          }

          const side = cluster.members.length < 10 ? 30 : 36;

          return (
            <OverlayView
              key={cluster.id}
              position={cluster.position}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <button
                type="button"
                aria-label={t("monitoreoIa.monitoringCenter.clusterUnits", {
                  count: cluster.members.length,
                })}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!map) return;
                  const bounds = new google.maps.LatLngBounds();
                  cluster.members.forEach((member) => bounds.extend(member.position));
                  map.fitBounds(bounds, 80);
                }}
                className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                style={{ width: side, height: side }}
              >
                {/* El CÚMULO usa el criterio estricto, no `LATIENDO`: agrupa
                    decenas de unidades y una sola en advertencia haría latir la
                    mancha entera. Al acercarse se separa en marcadores y ahí sí
                    late la que corresponde. */}
                <span
                  className={`absolute -inset-1 rounded-full opacity-[0.14] ${
                    URGENT_STATUSES.has(cluster.status)
                      ? "animate-tacticalPulse motion-reduce:animate-none"
                      : ""
                  }`}
                  style={{ background: tone }}
                />
                <span
                  className="absolute inset-0 rounded-full border-[1.5px] transition-transform duration-150 hover:scale-110"
                  style={{ borderColor: tone, background: "rgb(var(--c-surface) / 0.88)" }}
                />
                <span
                  className="relative font-mono text-[12px] font-bold"
                  style={{ color: tone }}
                >
                  {cluster.members.length}
                </span>
              </button>
            </OverlayView>
          );
        })}

        {selectedRow ? (
          <OverlayView
            position={selectedRow.position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <UnitMarker
              row={selectedRow}
              tone={WATCH_STATUS_MAP_COLORS[selectedRow.status]}
              selected
              onSelect={onSelectUnit}
            />
          </OverlayView>
        ) : null}
      </GoogleMap>

      <TacticalFocus
        map={map}
        unitId={selectedRow?.unitId ?? null}
        lat={selectedRow?.position.lat ?? null}
        lng={selectedRow?.position.lng ?? null}
        color={selectedRow ? WATCH_STATUS_MAP_COLORS[selectedRow.status] : brand.primary}
        labelBackground={palette.surface}
      />

      <TacticalCrosshair
        map={map}
        targetLat={selectedRow?.position.lat ?? null}
        targetLng={selectedRow?.position.lng ?? null}
        targetLabel={selectedRow?.name ?? null}
      />

      <div className="pointer-events-none absolute inset-0 z-[2]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgb(var(--c-primary) / 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--c-primary) / 0.07) 1px, transparent 1px)",
            backgroundSize: "46px 46px",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 62%, rgb(0 0 0 / 0.28) 100%)",
          }}
        />
        <div
          className="absolute inset-0 animate-tacticalSweep motion-reduce:animate-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, rgb(var(--c-primary) / 0.05) 48%, rgb(var(--c-primary) / 0.13) 50%, rgb(var(--c-primary) / 0.05) 52%, transparent 100%)",
            backgroundSize: "100% 220px",
            backgroundRepeat: "no-repeat",
          }}
        />
        {[
          "left-2 top-2 border-l border-t",
          "right-2 top-2 border-r border-t",
          "bottom-9 left-2 border-b border-l",
          "bottom-9 right-2 border-b border-r",
        ].map((corner) => (
          <span
            key={corner}
            className={`absolute h-5 w-5 ${corner}`}
            style={{ borderColor: "rgb(var(--c-primary) / 0.6)" }}
          />
        ))}
      </div>

      {/* ── El rastro del día ─────────────────────────────────────────────
          Va sobre el mapa, arriba a la derecha, y no en la barra de filtros:
          no filtra lo que se ve, abre una capa distinta. */}
      <div className="absolute right-3 top-3 z-[5] max-sm:bottom-24 max-sm:top-auto flex flex-col items-end gap-1.5">
        <button
          type="button"
          onClick={() => onToggleRastro?.()}
          aria-pressed={verRastro}
          className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 font-mono text-[10px] max-sm:min-h-9 max-sm:text-[11px] uppercase tracking-wider backdrop-blur transition-colors ${
            verRastro
              ? "border-info/60 bg-info/15 text-info"
              : "border-line bg-card/90 text-content-secondary hover:bg-card"
          }`}
        >
          <i className="ri-radar-line text-[12px]" aria-hidden="true" />
          {t("monitoreoIa.rastro.inteligencia", { defaultValue: "Inteligencia en vivo" })}
        </button>
      </div>

      <div className="absolute left-3 top-3 z-[5] flex flex-wrap items-center gap-1.5">
        {filterGroups.length > 0 ? (
          <MapFilterMenu
            groups={filterGroups}
            activeCount={activeFilterCount}
            onClear={() => onClearFilters?.()}
          />
        ) : null}

        {layers.map((layer) => (
          <Button
            key={layer.id}
            size="sm"
            variant={layer.active ? "primary" : "secondary"}
            onClick={layer.toggle}
            icon={<i className={layer.icon} />}
          >
            {layer.label}
          </Button>
        ))}
      </div>

      <TacticalClock />

      {positioned.length === 0 || (selectedIsListed && !selectedRow) ? (
        <div className="pointer-events-none absolute inset-0 z-[3] flex items-center justify-center">
          <span className="rounded-lg border border-line bg-surface/90 px-3 py-2 text-xs text-content-muted">
            {t(
              positioned.length === 0
                ? "monitoreoIa.monitoringCenter.noPositions"
                : "monitoreoIa.monitoringCenter.unitWithoutPosition",
            )}
          </span>
        </div>
      ) : null}

      <div className="absolute inset-x-0 bottom-0 z-[4] border-t border-line bg-surface/85 px-3 py-1.5 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 font-mono text-[9px] max-sm:text-[11px] uppercase tracking-wider">
          {WATCH_STATUSES.map((status) => (
            <span key={status} className="flex items-center gap-1.5 text-content-muted">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: WATCH_STATUS_MAP_COLORS[status] }}
              />
              {t(`monitoreoIa.status.${status}`)}
            </span>
          ))}
          <span className="ml-auto whitespace-nowrap text-primary-500">
            {t("monitoreoIa.monitoringCenter.legendCounts", {
              shown: positioned.length,
              total: totalUnits,
              cases: openCases,
              geofences: geofences.length,
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

interface UnitMarkerProps {
  row: PositionedRow;
  tone: string;
  selected: boolean;
  onSelect: (unitId: string | null) => void;
}

/** El mismo umbral que usa la medición de la jornada para «quieta». */
const QUIETA_KMH = 3;

function UnitMarker({ row, tone, selected, onSelect }: UnitMarkerProps) {
  /*
    Detenida es un punto; en marcha es una flecha apuntando a donde va. Con
    todo en punto, el radar enseña dónde está la flota pero no hacia dónde, que
    es la mitad de la pregunta cuando se vigila un recorrido.

    Sin rumbo NO se dibuja flecha aunque vaya a 90: apuntar al norte por
    omisión sería afirmar una dirección inventada. Queda el punto, que no
    afirma nada.
  */
  const enMarcha =
    (row.speedKph ?? 0) > QUIETA_KMH && Number.isFinite(row.headingDeg as number);

  /*
    ── El latido se apaga al seleccionar ──────────────────────────────────
    Al tocar la unidad arranca el radar, que ya dice «es ésta». Dejar el
    latido debajo son dos animaciones sobre el mismo punto peleándose por la
    atención, y el radar —que es el que informa de la selección— pierde.

    Un caso abierto también late aunque el estado no llegue a advertencia: un
    caso sin cerrar ES la noticia, y puede existir con la unidad en verde.
  */
  const late = !selected && (LATIENDO.has(row.status) || Boolean(row.openCase));

  return (
    <button
      type="button"
      aria-label={row.name}
      onClick={(event) => {
        // El marcador vive en el panel OVERLAY_MOUSE_TARGET, que es un hijo del
        // contenedor del mapa: sin cortar la propagación, el mismo clic lo
        // recoge el `onClick` del GoogleMap y deselecciona en el acto. Se veía
        // como que el radar no arrancaba nunca — y en realidad arrancaba y se
        // apagaba en el mismo fotograma.
        event.stopPropagation();
        onSelect(selected ? null : row.unitId);
      }}
      className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2"
      style={{ zIndex: selected ? 3 : undefined }}
    >
      {selected ? (
        <span
          className="absolute left-1/2 top-1/2 h-[34px] w-[34px] -ml-[17px] -mt-[17px] rounded-full border opacity-90 animate-tacticalSpin motion-reduce:animate-none"
          style={{ borderColor: tone }}
        >
          <span
            className="absolute left-1/2 top-[-5px] h-2 w-px -translate-x-1/2"
            style={{ background: tone }}
          />
          <span
            className="absolute left-[-5px] top-1/2 h-px w-2 -translate-y-1/2"
            style={{ background: tone }}
          />
        </span>
      ) : null}

      {late ? (
        <span
          className="absolute inset-0 rounded-full border animate-tacticalPulse motion-reduce:animate-none"
          style={{ borderColor: tone }}
        />
      ) : null}

      {enMarcha ? (
        // La rotación va en el contenedor y el hover en el hijo: en el mismo
        // elemento, el `style` en línea del rumbo pisa a la clase del hover y
        // la flecha nunca crecería.
        //
        // Más grande que el punto —20 px contra 10— porque una flecha es una
        // silueta hueca: al mismo tamaño pesa visualmente la mitad y se pierde
        // entre las unidades detenidas.
        <span
          className="absolute -inset-[3px] transition-transform duration-500 motion-reduce:transition-none"
          style={{ transform: `rotate(${row.headingDeg}deg)` }}
        >
          <svg
            viewBox="0 0 20 20"
            className="h-full w-full overflow-visible transition-transform duration-150 hover:scale-150 motion-reduce:transition-none"
          >
            <path
              d="M10 1 L16.5 17.5 L10 13.6 L3.5 17.5 Z"
              fill={tone}
              stroke="rgb(var(--c-surface) / 0.9)"
              strokeWidth="1.4"
              strokeLinejoin="round"
              style={{ filter: `drop-shadow(0 0 6px ${tone})` }}
            />
          </svg>
        </span>
      ) : (
        <span
          className="absolute inset-[2px] rounded-full transition-transform duration-150 hover:scale-150"
          style={{
            background: tone,
            boxShadow: `0 0 0 1.5px rgb(var(--c-surface) / 0.9), 0 0 10px ${tone}`,
          }}
        />
      )}

      <span
        className="absolute bottom-[17px] left-1/2 -translate-x-1/2 whitespace-nowrap rounded border px-1.5 font-mono text-[9.5px] max-sm:text-[11px] leading-[15px] tracking-wide"
        style={{
          borderColor: tone,
          background: selected ? tone : "rgb(var(--c-surface) / 0.92)",
          color: selected ? "rgb(var(--c-surface))" : "rgb(var(--c-content))",
        }}
      >
        {row.plate ?? row.name}
      </span>

      {row.speedKph != null && row.speedKph > 0 ? (
        <span
          className="absolute left-1/2 top-[17px] -translate-x-1/2 whitespace-nowrap rounded-sm px-0.5 font-mono text-[8.5px] max-sm:text-[11px] leading-[12px] opacity-90"
          style={{ background: tone, color: "rgb(var(--c-surface))" }}
        >
          {row.speedKph}
        </span>
      ) : null}
    </button>
  );
}
